import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { ScrollArea } from './ui/scroll-area';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Calendar, User, Building, X } from 'lucide-react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';

const MedCardDisclaimerView = ({ isOpen, onClose, adminToken }) => {
  const [acceptances, setAcceptances] = useState([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    if (isOpen) {
      fetchAcceptances();
    }
  }, [isOpen]);

  const fetchAcceptances = async () => {
    try {
      setLoading(true);
      
      const response = await axios.get(
        `${backendUrl}/api/admin/med-card-disclaimer-acceptances`,
        {
          headers: { Authorization: `Bearer ${adminToken}` }
        }
      );
      
      if (response.data.success) {
        setAcceptances(response.data.data);
      }
    } catch (error) {
      console.error('Fetch Med Card disclaimer acceptances error:', error);
      toast({
        title: "Error",
        description: "Failed to load Med Card disclaimer acceptances",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>Med Card Disclaimer Acceptances</span>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="h-[60vh]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : acceptances.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600">No disclaimer acceptances found</p>
            </div>
          ) : (
            <div className="space-y-4">
              {acceptances.map((acceptance, index) => (
                <div
                  key={acceptance.id || index}
                  className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <User className="h-4 w-4 text-gray-400" />
                        <span className="font-semibold text-gray-900">
                          {acceptance.userName || 'Unknown User'}
                        </span>
                        <Badge variant="secondary" className="text-xs">
                          {acceptance.userId}
                        </Badge>
                      </div>
                      
                      <div className="flex items-center space-x-2 mb-2">
                        <Building className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-700">
                          {acceptance.practiceName || 'Unknown Practice'}
                        </span>
                      </div>

                      <div className="flex items-center space-x-2">
                        <Calendar className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-600">
                          Accepted: {formatDate(acceptance.acceptedAt)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded p-3 mt-3">
                    <p className="text-xs font-semibold text-blue-900 mb-1">Digital Signature:</p>
                    <p className="text-sm text-blue-800 font-mono">{acceptance.signature}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>

        <div className="flex justify-between items-center pt-4 border-t">
          <p className="text-sm text-gray-600">
            Total: {acceptances.length} acceptance(s)
          </p>
          <Button onClick={onClose}>Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MedCardDisclaimerView;
